<template>
  <div class="content-container flex-column">
    <!-- Ten sam układ co pozostałe widoki „Zarządzania": lewa kolumna o szerokości panelu
         bocznego (z nazwą sekcji), treść obok — nie na całą szerokość strony. -->
    <div class="d-flex ug-section-container">
      <div class="ug-section">
        <span>{{ sectionTitle }}</span>
      </div>
      <div class="content-wrap ug-view">
        <!-- ================== WIDOK: lista grup ================== -->
        <section v-if="mode === 'list'" class="ds-panel">
          <header class="ds-panel__head">
            <h2 class="ds-panel__title">
              {{ t('user-groups') }}
              <span v-if="groups.length > 0" class="ds-muted">({{ groups.length }})</span>
            </h2>
            <div class="ds-panel__aside">
              <button type="button" class="button-save" @click="openCreate">
                + {{ t('user-groups-create') }}
              </button>
            </div>
          </header>

          <div class="ds-panel__body ds-panel__body--flush">
            <div v-if="loading" class="ds-loading">
              <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
            </div>

            <table v-else-if="groups.length > 0" class="ds-table ug-table">
              <thead>
                <tr>
                  <th class="ug-col-num">{{ t('ordinal-number') }}</th>
                  <th class="ug-col-name">{{ t('user-groups-name') }}</th>
                  <th class="ug-col-desc">{{ t('user-groups-description') }}</th>
                  <th class="ug-col-members">{{ t('user-groups-members') }}</th>
                  <th class="ug-col-action">{{ t('action') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(group, index) in groups" :key="group.id">
                  <td class="ug-col-num">{{ index + 1 }}</td>
                  <td class="ug-col-name">{{ group.name }}</td>
                  <td class="ug-col-desc">
                    <span v-if="group.description">{{ group.description }}</span>
                    <span v-else class="ds-muted">—</span>
                  </td>
                  <td class="ug-col-members">
                    <div v-if="group.userIds.length > 0" class="ug-chips">
                      <span v-for="userId in group.userIds" :key="userId" class="ug-chip">
                        {{ usernameById(userId) }}
                      </span>
                    </div>
                    <span v-else class="ds-muted">—</span>
                  </td>
                  <td class="ug-col-action">
                    <div v-if="pendingDeleteId === group.id" class="ug-actions">
                      <button type="button" class="button-delete button-compact" @click="confirmDelete(group.id)">
                        {{ t('wanted-confirm-delete') }}
                      </button>
                      <button type="button" class="button-add-user button-compact" @click="pendingDeleteId = null">
                        {{ t('cancel') }}
                      </button>
                    </div>
                    <div v-else class="ug-actions">
                      <button type="button" class="button-add-user button-compact" @click="openEdit(group)">
                        {{ t('edit') }}
                      </button>
                      <button type="button" class="button-delete button-compact" @click="pendingDeleteId = group.id">
                        {{ t('delete') }}
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <p v-else class="ds-empty">{{ t('user-groups-empty') }}</p>
          </div>
        </section>

        <!-- ================== WIDOK: utwórz / edytuj grupę ================== -->
        <section v-else class="ds-panel">
          <header class="ds-panel__head">
            <h2 class="ds-panel__title">
              {{ mode === 'edit' ? t('user-groups-edit-title') : t('user-groups-create-title') }}
            </h2>
          </header>

          <div class="ds-panel__body">
            <div class="ds-field">
              <label class="ds-field__label" for="ug-name">{{ t('user-groups-name') }} *</label>
              <div class="ds-field__controls">
                <input id="ug-name" v-model="form.name" type="text" class="form-control" maxlength="128" autocomplete="off" />
              </div>
            </div>
            <div class="ds-field">
              <label class="ds-field__label" for="ug-description">{{ t('user-groups-description') }}</label>
              <div class="ds-field__controls">
                <input id="ug-description" v-model="form.description" type="text" class="form-control" maxlength="255" />
              </div>
            </div>

            <div class="ug-members">
              <span class="ds-field-label">{{ t('user-groups-members') }}</span>
              <p class="ds-muted ug-hint">{{ t('user-groups-members-hint') }}</p>
              <input
                v-model="userSearch"
                type="search"
                class="form-control ug-search"
                :placeholder="t('user-groups-search-placeholder')"
                autocomplete="off"
              />
              <ul v-if="filteredUsers.length > 0" class="ug-pick-list">
                <li v-for="user in filteredUsers" :key="user.id">
                  <label class="ug-pick-item">
                    <input
                      type="checkbox"
                      class="ug-checkbox"
                      :checked="selectedUserIds.includes(user.id)"
                      @change="toggleUser(user.id)"
                    />
                    <span>{{ userLabel(user) }}</span>
                    <span v-if="otherGroupName(user)" class="ds-muted ug-current">
                      {{ t('user-groups-current-group', { name: otherGroupName(user) }) }}
                    </span>
                  </label>
                </li>
              </ul>
              <p v-else class="ds-empty">
                {{ userSearch ? t('no-matches') : t('user-groups-no-users') }}
              </p>
              <p v-if="selectedUserIds.length > 0" class="ds-muted">
                {{ t('user-groups-selected-count', { count: selectedUserIds.length }) }}
              </p>
            </div>
          </div>

          <footer class="ds-panel__foot">
            <span class="ds-toolbar__spacer"></span>
            <button type="button" class="button-add-user" @click="backToList">{{ t('cancel') }}</button>
            <button type="button" class="button-save" :disabled="!form.name.trim() || submitting" @click="submit">
              {{ submitting ? '...' : t('save') }}
            </button>
          </footer>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import UserGroupsService from "@/services/UserGroupsService";
import { useNotificationStore } from "@/stores/notification.store";

const { t } = useI18n();

const groups = ref([]);
const users = ref([]);
const loading = ref(false);
const submitting = ref(false);

// 'list' | 'create' | 'edit'
const mode = ref("list");
const editingId = ref(null);
const pendingDeleteId = ref(null);

const form = reactive({ name: "", description: "" });
const selectedUserIds = ref([]);
const userSearch = ref("");

const showStatus = (message, type = "error") => useNotificationStore().push(message, type);

const parseApiError = (error, fallback) => {
  if (typeof error === "string" && error.trim() !== "") return error;
  if (error?.message) return error.message;
  return fallback;
};

const mapGroup = (group) => ({
  id: group.id,
  name: group.name ?? "",
  description: group.description ?? "",
  userIds: Array.isArray(group.userIds) ? group.userIds : [],
});

const userLabel = (user) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name ? `${user.username} (${name})` : String(user.username ?? `#${user.id}`);
};

const usernameById = (userId) => users.value.find((user) => user.id === userId)?.username ?? `#${userId}`;

// Nazwa grupy, do której użytkownik należy obecnie — o ile to inna grupa niż edytowana.
// Zaznaczenie przeniesie go z tamtej grupy (użytkownik ma najwyżej jedną grupę).
const otherGroupName = (user) => {
  if (!user.userGroupId || user.userGroupId === editingId.value) return "";
  return groups.value.find((group) => group.id === user.userGroupId)?.name ?? "";
};

const sectionTitle = computed(() => {
  if (mode.value === "edit") return t("user-groups-edit-title");
  if (mode.value === "create") return t("user-groups-create-title");
  return t("user-groups");
});

const filteredUsers = computed(() => {
  const phrase = userSearch.value.trim().toLowerCase();
  if (!phrase) return users.value;
  return users.value.filter((user) => [user.username, user.firstName, user.lastName]
    .some((value) => String(value ?? "").toLowerCase().includes(phrase)));
});

const toggleUser = (userId) => {
  selectedUserIds.value = selectedUserIds.value.includes(userId)
    ? selectedUserIds.value.filter((id) => id !== userId)
    : [...selectedUserIds.value, userId];
};

const fetchData = async () => {
  loading.value = true;
  try {
    const [groupsResponse, usersResponse] = await Promise.all([
      UserGroupsService.getAll(),
      UserGroupsService.getAssignableUsers(),
    ]);
    groups.value = groupsResponse.map(mapGroup);
    users.value = usersResponse;
  } catch (error) {
    console.error("Error loading user groups:", error);
    showStatus(parseApiError(error, t("user-groups-load-error")));
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  editingId.value = null;
  form.name = "";
  form.description = "";
  selectedUserIds.value = [];
  userSearch.value = "";
  mode.value = "create";
};

const openEdit = (group) => {
  editingId.value = group.id;
  form.name = group.name;
  form.description = group.description;
  selectedUserIds.value = [...group.userIds];
  userSearch.value = "";
  mode.value = "edit";
};

const backToList = () => {
  mode.value = "list";
  editingId.value = null;
  pendingDeleteId.value = null;
};

const submit = async () => {
  const name = form.name.trim();
  if (!name) {
    showStatus(t("user-groups-name-required"));
    return;
  }

  submitting.value = true;
  const isEdit = mode.value === "edit";
  try {
    if (isEdit) {
      await UserGroupsService.update(editingId.value, name, form.description.trim(), selectedUserIds.value);
      showStatus(t("user-groups-updated"), "success");
    } else {
      await UserGroupsService.add(name, form.description.trim(), selectedUserIds.value);
      showStatus(t("user-groups-created"), "success");
    }
    // Przypisanie użytkownika do grupy może przenieść go z innej grupy — odświeżamy całość.
    await fetchData();
    backToList();
  } catch (error) {
    console.error("Error saving user group:", error);
    showStatus(parseApiError(error, t(isEdit ? "user-groups-update-error" : "user-groups-create-error")));
  } finally {
    submitting.value = false;
  }
};

const confirmDelete = async (id) => {
  pendingDeleteId.value = null;
  try {
    await UserGroupsService.delete(id);
    await fetchData();
    showStatus(t("user-groups-deleted"), "success");
  } catch (error) {
    console.error("Error deleting user group:", error);
    showStatus(parseApiError(error, t("user-groups-delete-error")));
  }
};

onMounted(fetchData);
</script>

<style scoped>
.content-container {
  background-color: var(--surface-tertiary);
}

.ug-section-container {
  width: 100%;
}

.ug-section {
  flex: 0 0 var(--sidebar-width);
  width: var(--sidebar-width);
  background: var(--surface-secondary);
}

.ug-section > span {
  display: block;
  padding: var(--spacing-md) var(--spacing-lg);
}

.ug-view {
  padding: var(--spacing-lg);
}

.ug-table {
  width: 100%;
  table-layout: fixed;
}

.ug-col-num { width: 4.5rem; white-space: nowrap; }
.ug-col-name { width: 20%; }
.ug-col-desc { width: 25%; }
.ug-col-action { width: 13rem; text-align: right; }

.ug-table td {
  overflow-wrap: anywhere;
}

.ug-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
  flex-wrap: nowrap;
}

.ug-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs);
}

.ug-chip {
  padding: 0 var(--spacing-sm);
  border: 1px solid var(--border-default);
  border-radius: var(--panel-radius);
  background: var(--surface-secondary);
  font-size: var(--font-size-xs);
  white-space: nowrap;
}

.ug-members {
  padding-top: var(--spacing-md);
}

.ug-hint {
  margin: 0 0 var(--spacing-sm);
  font-size: var(--font-size-sm);
}

.ug-search {
  margin-bottom: var(--spacing-sm);
}

.ug-pick-list {
  max-height: 320px;
  margin: 0 0 var(--spacing-sm);
  padding: var(--spacing-xs);
  overflow-y: auto;
  list-style: none;
  border: 1px solid var(--border-default);
  border-radius: var(--panel-radius);
  background: var(--surface-primary);
}

.ug-pick-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin: 0;
  padding: var(--spacing-xs) var(--spacing-sm);
  cursor: pointer;
  font-weight: var(--font-weight-regular);
}

.ug-pick-item:hover {
  background: var(--surface-secondary);
}

.ug-checkbox {
  -webkit-appearance: auto;
  appearance: auto;
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: var(--status-ok);
}

.ug-current {
  font-size: var(--font-size-xs);
}
</style>
